import { useParams } from "react-router";

import { Link as ReactLink } from 'react-router-dom'

import moment from 'moment';

import { gql, useMutation, useQuery } from "@apollo/client";
import { Button, Container, Dialog, DialogContent, DialogTitle, IconButton, Link, Paper, Select, Typography } from "@mui/material";
import { VBox } from "@/components/VBox";
import { HBox } from "@/components/HBox";

import { StatusBadge } from '../../pitches/DashboardStyles';

import i18n from '@/i18n'

import { useState } from "react";
import { CreatePitchForm } from "../../pitch/create";
import { UpdatePitchForm } from "../../pitch/[pitchId]/update";
import { Pencil, Trash } from "@phosphor-icons/react";
import { ReleaseCard } from "../../pitches/ReleaseCard";


const DELETE_MUTATION = gql`
  mutation deletePitch($pitchId: ID!) {
    deletePitch(pitchId: $pitchId) {
      ok
    }
  }
`

const QUERY = gql`
  query getRelease($releaseId: ID!) {
    studio {
      release(releaseId: $releaseId) {
        id
        slug
        name
        released
        artists {
          id
          slug
          name
        }
        label {
          id
          name
        }
        pitches {
          id
          source
          email
          pitched
          streamUrl
          handled
          slug
          pressText
        }
      }
    }
  }
`

export function ReleasePage() {
  const { releaseId } = useParams();
  return (
    <ReleaseContainer releaseId={releaseId} />
  )
}

export function ReleaseContainer({
  releaseId
}) {
  const [createPitchDialogOpen, setCreatePitchDialogOpen] = useState(false)
  const [deletingPitch, setDeletingPitch] = useState(null)
  const [updatePitchId, setUpdatePitchId] = useState(null)
  const [deletePitchMutation, { loading: deleting }] = useMutation(DELETE_MUTATION);

  const { data, refetch } = useQuery(
    QUERY, 
    {
      variables: {
        releaseId
      }
    }
  )
  const release = data?.studio?.release;

  if (!release) {
    return <Typography>{i18n.t('no-release')}</Typography>
  }
  const handleCreatePitchClicked = event => {
    event.preventDefault()
    setCreatePitchDialogOpen(true)
    return false;
  }
  const handleDeletePitchFormSubmit = event => {
    event.preventDefault()
    deletePitchMutation({
      variables: {
        pitchId: deletingPitch.id
      }
    }).then(() => {
      refetch();
      setDeletingPitch(null)
    })
    return false;
  }
  return (
    <Container>
      <ReleaseCard
        {...release}
      />
      <HBox>
        <Typography variant="h5" sx={{ flex: 1 }}>{i18n.t('pitches')}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreatePitchClicked}
        >
          {i18n.t('create-pitch')}
        </Button>
      </HBox>
      {release?.pitches?.length > 0 ? release?.pitches.map(pitch => (
        <Paper sx={{ marginBottom: 1 }}>
          <VBox sx={{ p: 1 }}>
            <HBox>
              <VBox sx={{ gap: 0, flex: 1 }}> 
                <Typography sx={{ flex: 1 }}>{pitch.email ?? i18n.t('unknown-email')}</Typography>
                <Typography sx={{ opacity: 0.5 }}>{moment(pitch.pitched).isValid() ? moment(pitch.pitched).fromNow() : i18n.t('unknown-date')}</Typography>
              </VBox>
              <StatusBadge status={'pending'} sx={{ margin: 1 }}>{pitch.source ?? 'unknown'}</StatusBadge>
            </HBox>
            <Typography sx={{ opacity: 0.5 }}>{i18n.t('press-text')}</Typography>
            <Typography sx={{}}>{pitch.pressText}</Typography>
            <Typography sx={{ opacity: 0.5 }}>{i18n.t('stream-url')}</Typography>
            {pitch.streamUrl ? (
              <Typography>
                <Link as="a" target="_blank" href={pitch.streamUrl}>{pitch.streamUrl}</Link>
              </Typography>
            ) : (
              <Typography sx={{ fontStyle: 'italic', opacity: 0.5 }}>
                {i18n.t('no-stream-url-available')}
              </Typography>
            )}
            <Typography sx={{ opacity: 0.5 }}>{i18n.t('handled')}</Typography>
            <Typography>{moment(pitch.handled).isValid() ? moment(pitch.handled).fromNow() : i18n.t('unknown-date')}</Typography>
            <HBox>
              <IconButton
                onClick={() => setUpdatePitchId(pitch.id)}
                variant="outlined"
              >
                <Pencil />
              </IconButton>
              <IconButton
                onClick={() => setDeletingPitch(pitch)}
                variant="outlined"
                color="danger"
              >
                <Trash />
              </IconButton>
            </HBox>
          </VBox>
        </Paper>
      )) : <Typography sx={{ padding: 5, textAlign: 'center' }}>{i18n.t('no-pitches-for-this-release')}</Typography>}
      <Dialog fullWidth open={createPitchDialogOpen} onClose={() => setCreatePitchDialogOpen(false)}>
        <DialogTitle>{i18n.t('create-pitch')}</DialogTitle>
        <DialogContent>
          <CreatePitchForm onClose={() => { setCreatePitchDialogOpen(false); }} releaseId={release.id} onCreated={() => { refetch(); }} />
        </DialogContent>
      </Dialog>
      <Dialog fullWidth open={!!updatePitchId} onClose={() => setUpdatePitchId(null)}>
        <DialogTitle>{i18n.t('update-pitch')}</DialogTitle>
        <DialogContent>
          {updatePitchId && <UpdatePitchForm onClose={() => { setUpdatePitchId(null); }} pitchId={updatePitchId} onUpdated={() => { setUpdatePitchId(null); refetch(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog fullWidth open={!!deletingPitch} onClose={() => setDeletingPitch(null)}>
        <DialogTitle>{i18n.t('confirm-delete-pitch')}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleDeletePitchFormSubmit}>
            <Typography>{i18n.t('delete-pitch.dialog.title')}</Typography>
            <Typography>{i18n.t('press-text')}</Typography>
            <Typography>{deletingPitch?.pressText}</Typography>
            <HBox>
              <Button type="submit" variant="contained">{i18n.t('confirm-delete-pitch')}</Button>
              <Button onClick={() => setDeletingPitch(null)} variant="contained">{i18n.t('no-keep-the-pitch')}</Button>
            </HBox>
          </form>
        </DialogContent>
      </Dialog>
    </Container>
  )
}